import React from 'react';
import { CheckCircle2, Award, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const AuthorizedDistributor = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="chemours" className="py-24 bg-gradient-to-br from-bharat-blue via-bharat-blue to-bharat-blue/95 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-manufacturing-orange rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-manufacturing-orange/20 backdrop-blur-sm border border-manufacturing-orange/30 rounded-full px-6 py-3">
            <Award className="w-5 h-5 text-manufacturing-orange mr-2" />
            <span className="text-white font-semibold">Authorized Partnership</span>
          </div>
        </div>

        {/* Main Heading */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Authorized Distributor of Chemours
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Proudly distributing Ti-Pure™ Titanium Dioxide across India
          </p>
        </div>

        {/* Logos Section */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <img 
              src="/chemours-logo.png" 
              alt="Chemours Logo"
              className="h-16 w-auto"
            />
          </div>
          <div className="text-4xl text-white/50 font-light">×</div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <img 
              src="/tipure-logo.jpg" 
              alt="Ti-Pure Logo"
              className="h-16 w-auto"
            />
          </div>
        </div>

        {/* Featured Grades */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white text-center mb-8">
            Our Ti-Pure™ Product Range
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* R-902+ Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Coatings
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-902+
                  </h4>
                  <p className="text-white/70 text-xs">Multipurpose Rutile</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Exceptional hiding power</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">50% less mixing energy</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Excellent chalk resistance</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Architectural Paints</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Industrial Coatings</span>
                </div>
              </div>
            </div>

            {/* R-104 Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Plastics
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-104
                  </h4>
                  <p className="text-white/70 text-xs">Thermoplastics Grade</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Outstanding melt flow</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Excellent lacing resistance</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">High tinting strength</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Masterbatches</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Cast Film</span>
                </div>
              </div>
            </div>

            {/* R-350 Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Plastics
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-350
                  </h4>
                  <p className="text-white/70 text-xs">Polyolefins & ABS</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Superior dispersion</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Thermal stability</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">UV resistance</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">ABS Parts</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">BOPP Films</span>
                </div>
              </div>
            </div>

            {/* R-103 Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Plastics
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-103
                  </h4>
                  <p className="text-white/70 text-xs">Polyolefins & PVC</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">High-shear dispersion</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Strong blue tone</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Semi-durable exterior</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Compounding</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Injection Molding</span>
                </div>
              </div>
            </div>

            {/* R-105 Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Plastics
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-105
                  </h4>
                  <p className="text-white/70 text-xs">Durable Exteriors</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Chalking resistance</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Excellent durability</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Blow molding optimized</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Blow Molding</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Blown Film</span>
                </div>
              </div>
            </div>

            {/* R-706 Grade */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="inline-block bg-manufacturing-orange px-3 py-1 rounded-full text-white text-xs font-semibold mb-2">
                    Coatings
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">
                    Ti-Pure™ R-706
                  </h4>
                  <p className="text-white/70 text-xs">High Gloss & Durability</p>
                </div>
                <Sparkles className="w-6 h-6 text-manufacturing-orange" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">High gloss finish</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Super durability</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-industrial-green flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-xs">Easy wet-in</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-3">
                <div className="text-white/60 text-xs mb-1">Applications:</div>
                <div className="flex flex-wrap gap-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Automotive</span>
                  <span className="px-2 py-0.5 bg-white/10 rounded-full text-white text-xs">Industrial</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Ti-Pure */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            Why Ti-Pure™ Titanium Dioxide?
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-manufacturing-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-manufacturing-orange" />
              </div>
              <h4 className="font-semibold text-white mb-2">Global Standard</h4>
              <p className="text-white/70 text-sm">World-leading titanium dioxide technology from Chemours</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-manufacturing-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-manufacturing-orange" />
              </div>
              <h4 className="font-semibold text-white mb-2">Consistent Quality</h4>
              <p className="text-white/70 text-sm">Unmatched batch-to-batch consistency and performance</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-manufacturing-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-manufacturing-orange" />
              </div>
              <h4 className="font-semibold text-white mb-2">Innovation Leader</h4>
              <p className="text-white/70 text-sm">Advanced chloride process technology and R&D excellence</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Looking for Authentic Ti-Pure™ Products?
          </h3>
          <p className="text-white/80 mb-8">
            Contact us for genuine Chemours Ti-Pure™ titanium dioxide with complete technical support
          </p>
          <Button 
            size="lg"
            onClick={() => scrollToSection('contact')}
            className="bg-manufacturing-orange hover:bg-manufacturing-orange/90 text-white text-lg px-8 py-6"
          >
            Request Ti-Pure™ Quote
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AuthorizedDistributor;
